import { useState, useRef, useEffect } from 'react';
import { FiChevronDown } from 'react-icons/fi';

export const SimpleDropdown = ({
  value,
  onChange,
  options = [],
  placeholder = "SELECT",
  className = "",
  disabled = false,
  variant = "pill", // "pill", "form", "themed", or "admin"
  buttonClassName = "",
  dropUp = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  // Get display text for selected value
  const getDisplayText = () => {
    if (!value) return placeholder;
    const selectedOption = options.find(opt => opt.value === value);
    return selectedOption?.label || value;
  };

  // Admin variant (compact, for admin headers/tables)
  if (variant === "admin") {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={buttonClassName || "h-8 px-3 pr-7 rounded bg-jp-card border border-jp-line text-[11px] text-jp-ink cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"}
        >
          <span>{getDisplayText()}</span>
          <FiChevronDown
            className={`absolute right-2 top-1/2 -translate-y-1/2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            size={12}
          />
        </button>

        {isOpen && !disabled && (
          <div
            className={`absolute left-0 min-w-full bg-jp-card border border-jp-line rounded-lg shadow-xl z-50 overflow-hidden ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
          >
            <div className="max-h-60 overflow-y-auto custom-scrollbar">
              {options.map((option) => {
                const isSelected = value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={`w-full text-left px-3 py-2 text-[11px] transition-colors whitespace-nowrap ${
                      isSelected
                        ? 'bg-jp-accent/10 text-jp-accent-ink font-semibold'
                        : 'text-jp-ink hover:bg-jp-section'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Pill variant (for form fields like PlayerInfo)
  if (variant === "pill") {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="w-full bg-transparent border-2 border-jp-ink/30 text-jp-ink text-xs text-center font-medium transition-all hover:bg-jp-section appearance-none cursor-pointer px-4 py-2.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className={value ? 'text-jp-ink' : 'text-jp-muted'}>
            {getDisplayText()}
          </span>
          <FiChevronDown
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            size={14}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && !disabled && (
          <div
            className="absolute top-full left-0 mt-2 w-full border border-jp-line rounded-lg shadow-xl z-50 overflow-hidden"
            style={{ backgroundColor: 'rgb(var(--jp-card))' }}
          >
            <div className="max-h-60 overflow-y-auto custom-scrollbar">
              {options.length > 0 ? (
                options.map((option) => {
                  const isSelected = value === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      className={`w-full text-center px-4 py-3 text-xs font-medium transition-colors ${
                        isSelected
                          ? 'bg-jp-accent/15 text-jp-accent-ink font-semibold'
                          : 'text-jp-ink hover:bg-jp-line'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-6 text-center text-jp-muted text-xs">
                  No options available
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Themed variant (form field on light/dark pages, matches the jp-* inputs)
  if (variant === "themed") {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className="w-full px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          <span className={value ? 'text-jp-ink' : 'text-jp-muted/70'}>
            {getDisplayText()}
          </span>
          <FiChevronDown
            className={`text-jp-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            size={18}
          />
        </button>

        {isOpen && !disabled && (
          <div className="absolute top-full left-0 mt-2 w-full bg-jp-card border border-jp-line rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="max-h-60 overflow-y-auto custom-scrollbar" role="listbox">
              {options.length > 0 ? (
                options.map((option) => {
                  const isSelected = value === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(option.value)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        isSelected
                          ? 'bg-jp-accent/10 text-jp-accent font-semibold'
                          : 'text-jp-ink hover:bg-jp-section'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-center text-jp-muted text-xs">
                  No options available
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Form variant (for regular forms)
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-5 py-3 rounded-xl bg-jp-section backdrop-blur-md border border-jp-line text-jp-ink text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-cyan-400/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
      >
        <span className={value ? 'text-jp-ink' : 'text-jp-muted'}>
          {getDisplayText()}
        </span>
        <FiChevronDown
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          size={18}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div
          className="absolute top-full left-0 mt-2 w-full border border-jp-line rounded-lg shadow-xl z-50 overflow-hidden"
          style={{ backgroundColor: 'rgb(var(--jp-card))' }}
        >
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.length > 0 ? (
              options.map((option) => {
                const isSelected = value === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      isSelected
                        ? 'bg-jp-accent/15 text-jp-accent-ink font-semibold'
                        : 'text-jp-ink hover:bg-jp-line'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-4 text-center text-jp-muted text-xs">
                No options available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleDropdown;
